import { useEffect, useMemo, useState } from "react";
import {
  collection,
  DocumentReference,
  addDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase";

interface SampleSendItem {
  id: string;
  createdAt?: Timestamp | null;
  name: string;
  email: string;
  sample: string;
  phone_Number: string;
  storagePath: string;
  mailTemplate?: DocumentReference | null;
  mailsendstatus: string;
  produrl?: string;
}

interface SyncResult {
  success: boolean;
  results: {
    created: number;
    updated: number;
    errors: number;
    errorDetails: string[];
  };
}

const formatDate = (value?: Timestamp | null) => {
  if (!value) return "-";
  return value.toDate().toLocaleString("ja-JP");
};

const formatMailSendStatus = (value?: string | null) => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return "未送信";
  if (normalized === "done") return "送信済み";
  return value ?? "未送信";
};

const isMailSendDone = (value?: string | null) => value?.trim().toLowerCase() === "done";

type MailingTab = "pending" | "sent";

const PAGE_SIZE = 50;

const applyTemplateVariables = (templateText: string, context: Record<string, any>) =>
  templateText.replace(/{{\s*([\w.]+)\s*}}/g, (_match, key) => {
    const pathParts = String(key).split(".");
    let current: any = context;
    for (const part of pathParts) {
      if (current === null || current === undefined) return "";
      if (typeof current !== "object") return "";
      current = Object.prototype.hasOwnProperty.call(current, part) ? current[part] : "";
    }
    if (current === null || current === undefined) return "";
    return String(current);
  });

export default function MailingListPage() {
  const [items, setItems] = useState<SampleSendItem[]>([]);
  const [templateTitles, setTemplateTitles] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [activeTab, setActiveTab] = useState<MailingTab>("pending");
  const [page, setPage] = useState(1);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const listQuery = query(collection(db, "samplesend"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(listQuery);
      const data = snapshot.docs.map((docSnap) => {
        const docData = docSnap.data() as Omit<SampleSendItem, "id">;
        return {
          id: docSnap.id,
          createdAt: docData.createdAt ?? null,
          name: docData.name || "",
          email: docData.email || "",
          sample: docData.sample || "",
          phone_Number: docData.phone_Number || "",
          storagePath: docData.storagePath || "",
          mailTemplate: docData.mailTemplate ?? null,
          mailsendstatus: docData.mailsendstatus || "",
          produrl: docData.produrl || "",
        };
      });
      setItems(data);
    } catch (err) {
      console.error("Failed to load sample send items", err);
      setError("メーリングリストの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    const loadTemplateTitles = async () => {
      const uniqueIds = Array.from(
        new Set(
          items
            .map((item) => item.mailTemplate?.id)
            .filter((value): value is string => Boolean(value))
        )
      );
      if (uniqueIds.length === 0) {
        setTemplateTitles({});
        return;
      }
      try {
        const templateDocs = await Promise.all(
          uniqueIds.map(async (id) => {
            const templateSnapshot = await getDoc(doc(db, "mail_templates", id));
            return {
              id,
              title: templateSnapshot.exists()
                ? (templateSnapshot.data() as { title?: string }).title || id
                : id,
            };
          })
        );
        setTemplateTitles((prev) => {
          const next = { ...prev };
          templateDocs.forEach((entry) => {
            next[entry.id] = entry.title;
          });
          return next;
        });
      } catch (err) {
        console.error("Failed to load mail template titles", err);
      }
    };

    loadTemplateTitles();
  }, [items]);

  const sortedItems = useMemo(() => items, [items]);
  const pendingItems = useMemo(
    () => sortedItems.filter((item) => !isMailSendDone(item.mailsendstatus)),
    [sortedItems]
  );
  const sentItems = useMemo(
    () => sortedItems.filter((item) => isMailSendDone(item.mailsendstatus)),
    [sortedItems]
  );
  const tabItems = activeTab === "sent" ? sentItems : pendingItems;
  const totalPages = Math.max(1, Math.ceil(tabItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return tabItems.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, tabItems]);

  const allSelected =
    pagedItems.length > 0 && pagedItems.every((item) => selectedIds.has(item.id));

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(pagedItems.map((item) => item.id)));
  };

  const toggleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSendSelected = async () => {
    if (selectedIds.size === 0) {
      alert("送信対象を選択してください。");
      return;
    }

    const selectedItems = pagedItems.filter((item) => selectedIds.has(item.id));
    const eligibleItems = selectedItems.filter((item) => !isMailSendDone(item.mailsendstatus));

    if (eligibleItems.length === 0) {
      alert("送信可能な宛先がありません。（送信済みのためスキップしました）");
      return;
    }

    if (!confirm("選択した宛先にメールを送信しますか?")) return;

    setSending(true);
    try {
      const updateMailStatus = httpsCallable(functions, "updateSampleSendMailStatus");
      let skippedDoneCount = selectedItems.length - eligibleItems.length;
      let sentCount = 0;

      for (const item of eligibleItems) {
        if (!item.email) continue;
        if (!item.mailTemplate?.id) {
          throw new Error("テンプレートが設定されていません。");
        }

        const templateSnapshot = await getDoc(doc(db, "mail_templates", item.mailTemplate.id));
        if (!templateSnapshot.exists()) {
          throw new Error("メールテンプレートが見つかりません。");
        }
        const templateData = templateSnapshot.data() as {
          subject?: string;
          body?: string;
          sign?: string;
          attach?: string;
        };
        const templateContext = { samplesend: item };
        const subject = applyTemplateVariables(templateData.subject || "", templateContext) || "(no subject)";
        const body = applyTemplateVariables(templateData.body || "", templateContext);
        const sign = applyTemplateVariables(templateData.sign || "", templateContext);
        const attach = applyTemplateVariables(templateData.attach || "", templateContext);
        const bodyWithSignature = sign ? `${body}\n\n${sign}` : body;

        const mailDoc = await addDoc(collection(db, "mail"), {
          customerEmail: item.email,
          subject,
          status: "waiting",
          spamLevel: 0,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });

        await addDoc(collection(db, "mail_messages"), {
          mailId: mailDoc,
          direction: "out",
          status: "waiting",
          from: "info@lear-caree.jp",
          to: item.email,
          subject,
          body: bodyWithSignature,
          attach,
          sign,
          createdAt: serverTimestamp(),
        });

        await updateDoc(doc(db, "samplesend", item.id), {
          mailsendstatus: "done",
        });
        await updateMailStatus({ uid: item.id, status: "done" });
        sentCount += 1;
      }

      const skipMessage = skippedDoneCount > 0 ? `\n送信済みでスキップ: ${skippedDoneCount}件` : "";
      const sentMessage = sentCount > 0 ? `\n送信開始: ${sentCount}件` : "";
      alert(`送信処理を開始しました。${sentMessage}${skipMessage}`);
      setSelectedIds(new Set());
      await fetchItems();
    } catch (sendError: any) {
      console.error("Failed to send selected mails", sendError);
      alert(sendError?.message || "送信に失敗しました。");
    } finally {
      setSending(false);
    }
  };

  const handleSync = async () => {
    if (
      !confirm(
        "スプレッドシートから同期しますか?\n同期済みの行はスキップされます。"
      )
    ) {
      return;
    }
    setSyncing(true);
    try {
      const syncFunction = httpsCallable(functions, "syncSampleSendFromSheet");
      const result = await syncFunction();
      const data = result.data as SyncResult;
      setSyncResult(data);
      alert(
        `同期完了!\n新規作成: ${data.results.created}件\n更新: ${data.results.updated}件\nエラー: ${data.results.errors}件`
      );
      await fetchItems();
    } catch (syncError: any) {
      console.error("Failed to sync sample send list", syncError);
      alert(syncError?.message || "同期に失敗しました。");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">メーリングリスト</h2>
          <p className="text-sm text-gray-500">samplesendコレクションの一覧を表示します。</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => {
                setActiveTab("pending");
                setSelectedIds(new Set());
                setPage(1);
              }}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === "pending"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              送信対象
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("sent");
                setSelectedIds(new Set());
                setPage(1);
              }}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === "sent"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              送信済み一覧
            </button>
          </div>
          <button
            type="button"
            onClick={handleSendSelected}
            disabled={sending || selectedIds.size === 0 || activeTab === "sent"}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
          >
            {sending ? "送信中..." : "メール送信"}
          </button>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            {syncing ? "同期中..." : "スプレッドシート同期"}
          </button>
        </div>
      </div>

      {syncResult && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-700">
          <div className="flex flex-wrap gap-4">
            <span>新規: {syncResult.results.created}件</span>
            <span>更新: {syncResult.results.updated}件</span>
            <span>エラー: {syncResult.results.errors}件</span>
          </div>
          {syncResult.results.errorDetails?.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-emerald-700">
              {syncResult.results.errorDetails.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">
            {activeTab === "sent" ? "送信済み一覧" : "送信対象一覧"}
          </h3>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-gray-500">読み込み中...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-500">{error}</div>
        ) : tabItems.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">登録データがありません。</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  {activeTab !== "sent" && (
                    <th className="px-4 py-3 text-left font-semibold">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(event) => toggleSelectAll(event.target.checked)}
                        aria-label="select all"
                        className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold">取り込み日</th>
                  <th className="px-4 py-3 text-left font-semibold">氏名</th>
                  <th className="px-4 py-3 text-left font-semibold">メールアドレス</th>
                  <th className="px-4 py-3 text-left font-semibold">資格</th>
                  <th className="px-4 py-3 text-left font-semibold">テンプレート</th>
                  <th className="px-4 py-3 text-left font-semibold">送信ステータス</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    {activeTab !== "sent" && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={(event) => toggleSelectOne(item.id, event.target.checked)}
                          aria-label={`select ${item.email}`}
                          className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-700">{formatDate(item.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-700">{item.name || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{item.email || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{item.sample || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.mailTemplate?.id
                        ? templateTitles[item.mailTemplate.id] || item.mailTemplate.id
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {formatMailSendStatus(item.mailsendstatus)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && tabItems.length > 0 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage <= 1}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            前のページ
          </button>
          <span className="text-gray-500">
            {currentPage} / {totalPages} ページ
          </span>
          <button
            type="button"
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage >= totalPages}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            次のページ
          </button>
        </div>
      )}
    </div>
  );
}
